import React, { lazy, Suspense } from "react";
import "./App.css";

import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { Router, Switch, Route } from "react-router-dom";
import { createBrowserHistory } from "history";

// SEO FIX: ReactGA re-enabled — tracks SPA route changes (page views) that gtag.js in
// index.html cannot detect on its own, since React is a single-page application.
import ReactGA from "react-ga4";

import Loader from "./Utils/Loader/Loader";
import PageMiss from "./404/404";

const Home = lazy(() => import(/* webpackChunkName: "home" */ "./Home/Home"));
const Contact = lazy(() => import(/* webpackChunkName: "contact" */ "./Contact/Contact"));
const Services = lazy(() => import(/* webpackChunkName: "services" */ "./Services/Services"));
const ServiceDetail = lazy(() => import(/* webpackChunkName: "services" */ "./Services/ServiceDetail"));
const Departments = lazy(() => import(/* webpackChunkName: "departments" */ "./Departments/Departments"));
const DepartmentDetail = lazy(() => import(/* webpackChunkName: "departments" */ "./Departments/DepartmentDetail"));
const Doctors = lazy(() => import(/* webpackChunkName: "doctors" */ "./Doctors/Doctors"));
const DoctorProfile = lazy(() => import(/* webpackChunkName: "doctorProfile" */ "./Doctors/DoctorProfile"));
const About = lazy(() => import(/* webpackChunkName: "about" */ "./About/About"));
const TPA = lazy(() => import(/* webpackChunkName: "tpa" */ "./TPA/TPA"));
const Appointment = lazy(() => import(/* webChunkName:"appointment" */ "./Appointment/Appointment"));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "privacyPolicy" */ "./PrivacyPolicy/PrivacyPolicy"));
const RefundPolicy = lazy(() => import(/* webpackChunkName: "refundPolicy" */ "./RefundPolicy/RefundPolicy"));
const TermsConditions = lazy(() => import(/* webpackChunkName: "termsConditions" */ "./TermsConditions/TermsConditions"));
const OrderSuccess = lazy(() => import(/* webpackChunkName: "orderSuccess" */ "./OrderStatus/Success"));
const OrderFailure = lazy(() => import(/* webpackChunkName: "orderFailure" */ "./OrderStatus/Failure"));
const CardPrint = lazy(() => import(/* webpackChunkName: "cardPrint" */ "./CardPrint/CardPrint"));

const theme = createTheme({
  palette: {
    primary: {
      main: "#0cb8b6",
    },
    secondary: {
      main: "#ffff",
    },
  },
  typography: {
    fontFamily: "Montserrat, sans-serif",
  },
});

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.customHistory = createBrowserHistory();
  }

  componentDidMount() {
    // SEO FIX: Initialize ReactGA so every route change in the SPA sends a page view to GA4.
    // The GA4 measurement ID matches the gtag.js config in index.html.
    ReactGA.initialize("G-L4HGS25MLM");

    // Track the initial page load as a page view
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });

    // SEO FIX: Listen to route changes and fire page view events for each navigation.
    // This is critical for SPAs — without this, GA only records the first page load.
    this.unlisten = this.customHistory.listen((location) => {
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname + location.search,
      });
    });
  }

  componentWillUnmount() {
    // Clean up the history listener to prevent memory leaks
    if (this.unlisten) {
      this.unlisten();
    }
  }

  render = () => {
    return (
      <ThemeProvider theme={theme}>
        <Router history={this.customHistory}>
          <Suspense
            fallback={
              <div className="loader-container">
                <Loader />
              </div>
            }>
            <Switch>
              <Route exact path="/" component={Home} />
              <Route path="/about" component={About} />
              <Route path="/services" component={Services} />
              <Route path="/book-now" component={Appointment} />
              <Route path="/service/:id" component={ServiceDetail} />
              <Route path="/departments" component={Departments} />
              <Route path="/department/:id" component={DepartmentDetail} />
              <Route path="/contact" component={Contact} />
              <Route path="/doctors" component={Doctors} />
              <Route path="/doctor/:id" component={DoctorProfile} />
              <Route path="/tpa-corporate" component={TPA} />
              <Route path="/privacy-policy" component={PrivacyPolicy} />
              <Route path="/refund-policy" component={RefundPolicy} />
              <Route path="/terms-conditions" component={TermsConditions} />
              <Route path="/order-success" component={OrderSuccess} />
              <Route path="/order-failure" component={OrderFailure} />
              <Route path="/card-print" component={CardPrint} />
              {/* SEO FIX: Catch-all route renders custom 404 page (already in place — good) */}
              <Route component={PageMiss} />
            </Switch>
          </Suspense>
        </Router>
      </ThemeProvider>
    );
  };
}

export default App;